import { getLogger } from '@confluence/logger';
import type { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import type { fetchCommentById } from '@confluence/comments-data-manager/entry-points/useCommentById.ts';
import type {
	AddCommentInCacheProps,
	Comment,
	getCommentsDataFromCache,
} from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import type { getProcessedCommentsDataActions } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';

import { extractNumericCommentId } from './extractNumericCommentId.ts';

const logger = getLogger('syncInlineReactionCommentToPanel');

type FetchCommentById = typeof fetchCommentById;
type AddCommentInCache = (props: AddCommentInCacheProps) => void;
type GetCommentsDataFromCache = typeof getCommentsDataFromCache;
type ProcessCommentsData = ReturnType<
	typeof getProcessedCommentsDataActions
>['processCommentsData'];
type FetchedComment = Awaited<ReturnType<FetchCommentById>>;

const isRootComment = (comment: FetchedComment): comment is Comment =>
	Boolean(comment && 'replies' in comment);

export type SyncInlineReactionCommentToPanelOptions = {
	commentId: string;
	contentId: string;
	pageMode: PageMode;
	fetchCommentById?: FetchCommentById;
	addCommentInCache?: AddCommentInCache;
	getCommentsDataFromCache?: GetCommentsDataFromCache;
	processCommentsData?: ProcessCommentsData;
	triggerCommentPanelRefresh?: () => Promise<unknown> | void;
};

export const syncInlineReactionCommentToPanel = async ({
	commentId,
	contentId,
	pageMode,
	fetchCommentById,
	addCommentInCache,
	getCommentsDataFromCache,
	processCommentsData,
}: SyncInlineReactionCommentToPanelOptions): Promise<void> => {
	if (
		!fetchCommentById ||
		!addCommentInCache ||
		!getCommentsDataFromCache ||
		!processCommentsData
	) {
		const missingCallbacks = [
			!fetchCommentById && 'fetchCommentById',
			!addCommentInCache && 'addCommentInCache',
			!getCommentsDataFromCache && 'getCommentsDataFromCache',
			!processCommentsData && 'processCommentsData',
		].filter(Boolean);
		logger.warn`Skipping inline reaction comments panel sync because required callbacks are missing: ${missingCallbacks.join(
			', ',
		)}`;
		return;
	}

	try {
		const numericCommentId = extractNumericCommentId(commentId);
		const comment = await fetchCommentById({
			commentId: numericCommentId,
			contentId,
			fetchPolicy: 'network-only',
		});
		if (!isRootComment(comment)) {
			logger.warn`Skipping comments panel sync for inline reaction: fetchCommentById returned an unexpected shape for comment ${numericCommentId} (null, undefined, or reply-only)`;
			return;
		}

		addCommentInCache({ newComment: comment });
		const updatedCommentsData = getCommentsDataFromCache(contentId);
		processCommentsData({
			data: updatedCommentsData,
			pageMode,
		});
	} catch (error: unknown) {
		// TODO ACTIVATION-2708: replace with proper experience tracking to match inline comments.
		logger.warn`Failed to update comments panel data for inline reaction: ${
			error instanceof Error ? error : new Error(String(error))
		}`;
	}
};
